/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import { get } from 'lodash-es'

import type {
  Country,
  Degree,
  Fluency,
  Language,
  Level,
  LocaleLanguage,
  SectionID,
} from '@/models'

import {
  BrazilianPortugueseCountryNames,
  DutchCountryNames,
  EnglishCountryNames,
  FrenchCountryNames,
  GermanCountryNames,
  IndonesianCountryNames,
  JapaneseCountryNames,
  NorwegianCountryNames,
  SimplifiedChineseCountryNames,
  SpanishCountryNames,
  TraditionalChineseCountryHKNames,
  TraditionalChineseCountryTWNames,
} from '@/models'

/** Defines the structure for translated terms for a single language. */
type OptionTranslation = {
  /** Translations for country names. */
  countries: Record<Country, string>
  /** Translations for degree types. */
  degrees: Record<Degree, string>
  /** Translations for language fluency levels. */
  fluency: Record<Fluency, string>
  /** Translations for language names. */
  languages: Record<Language, string>
  /** Translations for resume section titles. */
  sections: Record<SectionID, string>
  /** Translations for skill proficiency levels. */
  skills: Record<Level, string>
}

type OptionCategory = keyof OptionTranslation

/** The structure containing translations for all supported languages. */
type OptionsTranslations = Record<LocaleLanguage, OptionTranslation>

/**
 * Retrieves the translated terms for a specific locale language.
 *
 * Includes translations for degrees, languages, fluencies, countries, section
 * titles, levels, and other specific terms.
 *
 * @param language - The desired locale language. If undefined, defaults to
 * English.
 * @returns An object containing the translated terms for the specified
 * language.
 */
export function getOptionTranslation<K extends OptionCategory>(
  language: LocaleLanguage,
  category: K,
  option: keyof OptionTranslation[K]
): string {
  const optionsTranslations: OptionsTranslations = {
    en: {
      countries: EnglishCountryNames,
      degrees: {
        'Middle School': 'Middle School',
        'High School': 'High School',
        Diploma: 'Diploma',
        Associate: 'Associate',
        Bachelor: 'Bachelor',
        Master: 'Master',
        Doctor: 'Doctor',
      },
      fluency: {
        'Elementary Proficiency': 'Elementary Proficiency',
        'Limited Working Proficiency': 'Limited Working Proficiency',
        'Minimum Professional Proficiency': 'Minimum Professional Proficiency',
        'Full Professional Proficiency': 'Full Professional Proficiency',
        'Native or Bilingual Proficiency': 'Native or Bilingual Proficiency',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albanian',
        Amharic: 'Amharic',
        Arabic: 'Arabic',
        Azerbaijani: 'Azerbaijani',
        Belarusian: 'Belarusian',
        Bengali: 'Bengali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgarian',
        Burmese: 'Burmese',
        Cantonese: 'Cantonese',
        Catalan: 'Catalan',
        Chinese: 'Chinese',
        Croatian: 'Croatian',
        Czech: 'Czech',
        Danish: 'Danish',
        Dutch: 'Dutch',
        English: 'English',
        Estonian: 'Estonian',
        Farsi: 'Farsi',
        Filipino: 'Filipino',
        Finnish: 'Finnish',
        French: 'French',
        German: 'German',
        Greek: 'Greek',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebrew',
        Hindi: 'Hindi',
        Hungarian: 'Hungarian',
        Icelandic: 'Icelandic',
        Igbo: 'Igbo',
        Indonesian: 'Indonesian',
        Irish: 'Irish',
        Italian: 'Italian',
        Japanese: 'Japanese',
        Javanese: 'Javanese',
        Kazakh: 'Kazakh',
        Khmer: 'Khmer',
        Korean: 'Korean',
        Lahnda: 'Lahnda',
        Latvian: 'Latvian',
        Lithuanian: 'Lithuanian',
        Malay: 'Malay',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Nepali',
        Norwegian: 'Norwegian',
        Oromo: 'Oromo',
        Pashto: 'Pashto',
        Polish: 'Polish',
        Portuguese: 'Portuguese',
        Romanian: 'Romanian',
        Russian: 'Russian',
        Serbian: 'Serbian',
        Shona: 'Shona',
        Sinhala: 'Sinhala',
        Slovak: 'Slovak',
        Slovene: 'Slovene',
        Somali: 'Somali',
        Spanish: 'Spanish',
        Sundanese: 'Sundanese',
        Swahili: 'Swahili',
        Swedish: 'Swedish',
        Tagalog: 'Tagalog',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Thai',
        Turkish: 'Turkish',
        Ukrainian: 'Ukrainian',
        Urdu: 'Urdu',
        Uzbek: 'Uzbek',
        Vietnamese: 'Vietnamese',
        Yoruba: 'Yoruba',
        Zulu: 'Zulu',
      },
      sections: {
        awards: 'Awards',
        basics: 'Basics',
        certificates: 'Certificates',
        education: 'Education',
        interests: 'Interests',
        languages: 'Languages',
        location: 'Location',
        profiles: 'Profiles',
        projects: 'Projects',
        publications: 'Publications',
        references: 'References',
        skills: 'Skills',
        volunteer: 'Volunteer',
        work: 'Work',
      },
      skills: {
        Novice: 'Novice',
        Beginner: 'Beginner',
        Intermediate: 'Intermediate',
        Advanced: 'Advanced',
        Expert: 'Expert',
        Master: 'Master',
      },
    },
    'zh-hans': {
      countries: SimplifiedChineseCountryNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '专科',
        Associate: '副学士',
        Bachelor: '学士',
        Master: '硕士',
        Doctor: '博士',
      },
      fluency: {
        'Elementary Proficiency': '初级水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低专业水平',
        'Full Professional Proficiency': '完全专业水平',
        'Native or Bilingual Proficiency': '母语或双语水平',
      },
      languages: {
        Afrikaans: '南非语',
        Albanian: '阿尔巴尼亚语',
        Amharic: '阿姆哈拉语',
        Arabic: '阿拉伯语',
        Azerbaijani: '阿塞拜疆语',
        Belarusian: '白俄罗斯语',
        Bengali: '孟加拉语',
        Bhojpuri: '博杰普尔语',
        Bulgarian: '保加利亚语',
        Burmese: '缅甸语',
        Cantonese: '粤语',
        Catalan: '加泰罗尼亚语',
        Chinese: '中文',
        Croatian: '克罗地亚语',
        Czech: '捷克语',
        Danish: '丹麦语',
        Dutch: '荷兰语',
        English: '英语',
        Estonian: '爱沙尼亚语',
        Farsi: '波斯语',
        Filipino: '菲律宾语',
        Finnish: '芬兰语',
        French: '法语',
        German: '德语',
        Greek: '希腊语',
        Gujarati: '古吉拉特语',
        Hausa: '豪萨语',
        Hebrew: '希伯来语',
        Hindi: '印地语',
        Hungarian: '匈牙利语',
        Icelandic: '冰岛语',
        Igbo: '伊博语',
        Indonesian: '印度尼西亚语',
        Irish: '爱尔兰语',
        Italian: '意大利语',
        Japanese: '日语',
        Javanese: '爪哇语',
        Kazakh: '哈萨克语',
        Khmer: '高棉语',
        Korean: '韩语',
        Lahnda: '拉亨达语',
        Latvian: '拉脱维亚语',
        Lithuanian: '立陶宛语',
        Malay: '马来语',
        Mandarin: '普通话',
        Marathi: '马拉地语',
        Nepali: '尼泊尔语',
        Norwegian: '挪威语',
        Oromo: '奥罗莫语',
        Pashto: '普什图语',
        Polish: '波兰语',
        Portuguese: '葡萄牙语',
        Romanian: '罗马尼亚语',
        Russian: '俄语',
        Serbian: '塞尔维亚语',
        Shona: '绍纳语',
        Sinhala: '僧伽罗语',
        Slovak: '斯洛伐克语',
        Slovene: '斯洛文尼亚语',
        Somali: '索马里语',
        Spanish: '西班牙语',
        Sundanese: '巽他语',
        Swahili: '斯瓦希里语',
        Swedish: '瑞典语',
        Tagalog: '他加禄语',
        Tamil: '泰米尔语',
        Telugu: '泰卢固语',
        Thai: '泰语',
        Turkish: '土耳其语',
        Ukrainian: '乌克兰语',
        Urdu: '乌尔都语',
        Uzbek: '乌兹别克语',
        Vietnamese: '越南语',
        Yoruba: '约鲁巴语',
        Zulu: '祖鲁语',
      },
      sections: {
        awards: '荣誉',
        basics: '简介',
        certificates: '证书',
        education: '教育背景',
        interests: '兴趣爱好',
        languages: '语言',
        location: '位置',
        profiles: '个人主页',
        projects: '项目',
        publications: '出版刊物',
        references: '引荐',
        skills: '专业技能',
        volunteer: '志愿服务',
        work: '工作经历',
      },
      skills: {
        Novice: '新手',
        Beginner: '初级',
        Intermediate: '中级',
        Advanced: '高级',
        Expert: '专家',
        Master: '大师',
      },
    },
    'zh-hant-hk': {
      countries: TraditionalChineseCountryHKNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '專科',
        Associate: '副學士',
        Bachelor: '學士',
        Master: '碩士',
        Doctor: '博士',
      },
      fluency: {
        'Elementary Proficiency': '初級水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低專業水平',
        'Full Professional Proficiency': '完全專業水平',
        'Native or Bilingual Proficiency': '母語或雙語水平',
      },
      languages: {
        Afrikaans: '南非語',
        Albanian: '阿爾巴尼亞語',
        Amharic: '阿姆哈拉語',
        Arabic: '阿拉伯語',
        Azerbaijani: '阿塞拜疆語',
        Belarusian: '白俄羅斯語',
        Bengali: '孟加拉語',
        Bhojpuri: '博傑普爾語',
        Bulgarian: '保加利亞語',
        Burmese: '緬甸語',
        Cantonese: '粵語',
        Catalan: '加泰羅尼亞語',
        Chinese: '中文',
        Croatian: '克羅地亞語',
        Czech: '捷克語',
        Danish: '丹麥語',
        Dutch: '荷蘭語',
        English: '英語',
        Estonian: '愛沙尼亞語',
        Farsi: '波斯語',
        Filipino: '菲律賓語',
        Finnish: '芬蘭語',
        French: '法語',
        German: '德語',
        Greek: '希臘語',
        Gujarati: '古吉拉特語',
        Hausa: '豪薩語',
        Hebrew: '希伯來語',
        Hindi: '印地語',
        Hungarian: '匈牙利語',
        Icelandic: '冰島語',
        Igbo: '伊博語',
        Indonesian: '印度尼西亞語',
        Irish: '愛爾蘭語',
        Italian: '意大利語',
        Japanese: '日語',
        Javanese: '爪哇語',
        Kazakh: '哈薩克語',
        Khmer: '高棉語',
        Korean: '韓語',
        Lahnda: '拉亨達語',
        Latvian: '拉脫維亞語',
        Lithuanian: '立陶宛語',
        Malay: '馬來語',
        Mandarin: '普通話',
        Marathi: '馬拉地語',
        Nepali: '尼泊爾語',
        Norwegian: '挪威語',
        Oromo: '奧羅莫語',
        Pashto: '普什圖語',
        Polish: '波蘭語',
        Portuguese: '葡萄牙語',
        Romanian: '羅馬尼亞語',
        Russian: '俄語',
        Serbian: '塞爾維亞語',
        Shona: '紹納語',
        Sinhala: '僧伽羅語',
        Slovak: '斯洛伐克語',
        Slovene: '斯洛維尼亞語',
        Somali: '索馬里語',
        Spanish: '西班牙語',
        Sundanese: '巽他語',
        Swahili: '斯瓦希里語',
        Swedish: '瑞典語',
        Tagalog: '他加祿語',
        Tamil: '泰米爾語',
        Telugu: '泰盧固語',
        Thai: '泰語',
        Turkish: '土耳其語',
        Ukrainian: '烏克蘭語',
        Urdu: '烏爾都語',
        Uzbek: '烏茲別克語',
        Vietnamese: '越南語',
        Yoruba: '約魯巴語',
        Zulu: '祖魯語',
      },
      sections: {
        awards: '榮譽',
        basics: '簡介',
        certificates: '證書',
        education: '教育背景',
        interests: '興趣愛好',
        languages: '語言',
        location: '位置',
        profiles: '個人主頁',
        projects: '項目',
        publications: '出版刊物',
        references: '引薦',
        skills: '專業技能',
        volunteer: '志願服務',
        work: '工作經歷',
      },
      skills: {
        Novice: '新手',
        Beginner: '初級',
        Intermediate: '中級',
        Advanced: '高級',
        Expert: '專家',
        Master: '大師',
      },
    },
    'zh-hant-tw': {
      countries: TraditionalChineseCountryTWNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '專科',
        Associate: '副學士',
        Bachelor: '學士',
        Master: '碩士',
        Doctor: '博士',
      },
      fluency: {
        'Elementary Proficiency': '初級水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低專業水平',
        'Full Professional Proficiency': '完全專業水平',
        'Native or Bilingual Proficiency': '母語或雙語水平',
      },
      languages: {
        Afrikaans: '南非語',
        Albanian: '阿爾巴尼亞語',
        Amharic: '阿姆哈拉語',
        Arabic: '阿拉伯語',
        Azerbaijani: '阿塞拜疆語',
        Belarusian: '白俄羅斯語',
        Bengali: '孟加拉語',
        Bhojpuri: '博傑普爾語',
        Bulgarian: '保加利亞語',
        Burmese: '緬甸語',
        Cantonese: '粵語',
        Catalan: '加泰羅尼亞語',
        Chinese: '中文',
        Croatian: '克羅地亞語',
        Czech: '捷克語',
        Danish: '丹麥語',
        Dutch: '荷蘭語',
        English: '英語',
        Estonian: '愛沙尼亞語',
        Farsi: '波斯語',
        Filipino: '菲律賓語',
        Finnish: '芬蘭語',
        French: '法語',
        German: '德語',
        Greek: '希臘語',
        Gujarati: '古吉拉特語',
        Hausa: '豪薩語',
        Hebrew: '希伯來語',
        Hindi: '印地語',
        Hungarian: '匈牙利語',
        Icelandic: '冰島語',
        Igbo: '伊博語',
        Indonesian: '印度尼西亞語',
        Irish: '愛爾蘭語',
        Italian: '意大利語',
        Japanese: '日語',
        Javanese: '爪哇語',
        Kazakh: '哈薩克語',
        Khmer: '高棉語',
        Korean: '韓語',
        Lahnda: '拉亨達語',
        Latvian: '拉脫維亞語',
        Lithuanian: '立陶宛語',
        Malay: '馬來語',
        Mandarin: '普通話',
        Marathi: '馬拉地語',
        Nepali: '尼泊爾語',
        Norwegian: '挪威語',
        Oromo: '奧羅莫語',
        Pashto: '普什圖語',
        Polish: '波蘭語',
        Portuguese: '葡萄牙語',
        Romanian: '羅馬尼亞語',
        Russian: '俄語',
        Serbian: '塞爾維亞語',
        Shona: '紹納語',
        Sinhala: '僧伽羅語',
        Slovak: '斯洛伐克語',
        Slovene: '斯洛維尼亞語',
        Somali: '索馬里語',
        Spanish: '西班牙語',
        Sundanese: '巽他語',
        Swahili: '斯瓦希里語',
        Swedish: '瑞典語',
        Tagalog: '他加祿語',
        Tamil: '泰米爾語',
        Telugu: '泰盧固語',
        Thai: '泰語',
        Turkish: '土耳其語',
        Ukrainian: '烏克蘭語',
        Urdu: '烏爾都語',
        Uzbek: '烏茲別克語',
        Vietnamese: '越南語',
        Yoruba: '約魯巴語',
        Zulu: '祖魯語',
      },
      sections: {
        awards: '榮譽',
        basics: '簡介',
        certificates: '證書',
        education: '教育背景',
        interests: '興趣愛好',
        languages: '語言',
        location: '位置',
        profiles: '個人主頁',
        projects: '項目',
        publications: '出版刊物',
        references: '引薦',
        skills: '專業技能',
        volunteer: '志願服務',
        work: '工作經歷',
      },
      skills: {
        Novice: '新手',
        Beginner: '初級',
        Intermediate: '中級',
        Advanced: '高級',
        Expert: '專家',
        Master: '大師',
      },
    },
    es: {
      countries: SpanishCountryNames,
      degrees: {
        'Middle School': 'Escuela secundaria',
        'High School': 'Título de secundaria',
        Diploma: 'Diploma',
        Associate: 'Grado de asociado',
        Bachelor: 'Licenciatura',
        Master: 'Maestría',
        Doctor: 'Doctorado',
      },
      fluency: {
        'Elementary Proficiency': 'Competencia elemental',
        'Limited Working Proficiency': 'Competencia limitada de trabajo',
        'Minimum Professional Proficiency':
          'Competencia profesional de trabajo',
        'Full Professional Proficiency': 'Competencia profesional plena',
        'Native or Bilingual Proficiency': 'Competencia nativa o bilingüe',
      },
      languages: {
        Afrikaans: 'Afrikáns',
        Albanian: 'Albanés',
        Amharic: 'Amárico',
        Arabic: 'Árabe',
        Azerbaijani: 'Azerbaiyano',
        Belarusian: 'Bielorruso',
        Bengali: 'Bengalí',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Búlgaro',
        Burmese: 'Birmano',
        Cantonese: 'Cantonés',
        Catalan: 'Catalán',
        Chinese: 'Chino',
        Croatian: 'Croata',
        Czech: 'Checo',
        Danish: 'Danés',
        Dutch: 'Neerlandés',
        English: 'Inglés',
        Estonian: 'Estonio',
        Farsi: 'Persa',
        Filipino: 'Filipino',
        Finnish: 'Finlandés',
        French: 'Francés',
        German: 'Alemán',
        Greek: 'Griego',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebreo',
        Hindi: 'Hindi',
        Hungarian: 'Húngaro',
        Icelandic: 'Islandés',
        Igbo: 'Igbo',
        Indonesian: 'Indonesio',
        Irish: 'Irlandés',
        Italian: 'Italiano',
        Japanese: 'Japonés',
        Javanese: 'Javanés',
        Kazakh: 'Kazajo',
        Khmer: 'Jemer',
        Korean: 'Coreano',
        Lahnda: 'Lahnda',
        Latvian: 'Letón',
        Lithuanian: 'Lituano',
        Malay: 'Malayo',
        Mandarin: 'Mandarín',
        Marathi: 'Marathi',
        Nepali: 'Nepalí',
        Norwegian: 'Noruego',
        Oromo: 'Oromo',
        Pashto: 'Pastún',
        Polish: 'Polaco',
        Portuguese: 'Portugués',
        Romanian: 'Rumano',
        Russian: 'Ruso',
        Serbian: 'Serbio',
        Shona: 'Shona',
        Sinhala: 'Cingalés',
        Slovak: 'Eslovaco',
        Slovene: 'Esloveno',
        Somali: 'Somalí',
        Spanish: 'Español',
        Sundanese: 'Sundanés',
        Swahili: 'Swahili',
        Swedish: 'Sueco',
        Tagalog: 'Tagalo',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Tailandés',
        Turkish: 'Turco',
        Ukrainian: 'Ucraniano',
        Urdu: 'Urdu',
        Uzbek: 'Uzbeko',
        Vietnamese: 'Vietnamita',
        Yoruba: 'Yoruba',
        Zulu: 'Zulú',
      },
      sections: {
        awards: 'Premios',
        basics: 'Información básica',
        certificates: 'Certificados',
        education: 'Educación',
        interests: 'Intereses',
        languages: 'Idiomas',
        location: 'Ubicación',
        profiles: 'Perfiles',
        projects: 'Proyectos',
        publications: 'Publicaciones',
        references: 'Referencias',
        skills: 'Competencias',
        volunteer: 'Voluntariado',
        work: 'Experiencia laboral',
      },
      skills: {
        Novice: 'Novato',
        Beginner: 'Principiante',
        Intermediate: 'Intermedio',
        Advanced: 'Avanzado',
        Expert: 'Experto',
        Master: 'Maestro',
      },
    },
    fr: {
      countries: FrenchCountryNames,
      degrees: {
        'Middle School': 'Collège',
        'High School': 'Lycée',
        Diploma: 'Diplôme',
        Associate: 'Diplôme universitaire de technologie',
        Bachelor: 'Licence',
        Master: 'Master',
        Doctor: 'Doctorat',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albanais',
        Amharic: 'Amharique',
        Arabic: 'Arabe',
        Azerbaijani: 'Azéri',
        Belarusian: 'Biélorusse',
        Bengali: 'Bengali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgare',
        Burmese: 'Birman',
        Cantonese: 'Cantonais',
        Catalan: 'Catalan',
        Chinese: 'Chinois',
        Croatian: 'Croate',
        Czech: 'Tchèque',
        Danish: 'Danois',
        Dutch: 'Néerlandais',
        English: 'Anglais',
        Estonian: 'Estonien',
        Farsi: 'Persan',
        Filipino: 'Filipino',
        Finnish: 'Finnois',
        French: 'Français',
        German: 'Allemand',
        Greek: 'Grec',
        Gujarati: 'Gujarati',
        Hausa: 'Haoussa',
        Hebrew: 'Hébreu',
        Hindi: 'Hindi',
        Hungarian: 'Hongrois',
        Icelandic: 'Islandais',
        Igbo: 'Igbo',
        Indonesian: 'Indonésien',
        Irish: 'Irlandais',
        Italian: 'Italien',
        Japanese: 'Japonais',
        Javanese: 'Javanais',
        Kazakh: 'Kazakh',
        Khmer: 'Khmer',
        Korean: 'Coréen',
        Lahnda: 'Lahnda',
        Latvian: 'Letton',
        Lithuanian: 'Lituanien',
        Malay: 'Malais',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Népalais',
        Norwegian: 'Norvégien',
        Oromo: 'Oromo',
        Pashto: 'Pachto',
        Polish: 'Polonais',
        Portuguese: 'Portugais',
        Romanian: 'Roumain',
        Russian: 'Russe',
        Serbian: 'Serbe',
        Shona: 'Shona',
        Sinhala: 'Cingalais',
        Slovak: 'Slovaque',
        Slovene: 'Slovène',
        Somali: 'Somali',
        Spanish: 'Espagnol',
        Sundanese: 'Soundanais',
        Swahili: 'Swahili',
        Swedish: 'Suédois',
        Tagalog: 'Tagalog',
        Tamil: 'Tamoul',
        Telugu: 'Telugu',
        Thai: 'Thaï',
        Turkish: 'Turc',
        Ukrainian: 'Ukrainien',
        Urdu: 'Ourdou',
        Uzbek: 'Ouzbek',
        Vietnamese: 'Vietnamien',
        Yoruba: 'Yoruba',
        Zulu: 'Zoulou',
      },
      fluency: {
        'Elementary Proficiency': 'Niveau élémentaire',
        'Limited Working Proficiency': 'Niveau de travail limité',
        'Minimum Professional Proficiency': 'Niveau professionnel minimum',
        'Full Professional Proficiency': 'Niveau professionnel complet',
        'Native or Bilingual Proficiency': 'Niveau natif ou bilingue',
      },
      sections: {
        awards: 'Récompenses',
        basics: 'Informations de base',
        certificates: 'Certificats',
        education: 'Formation',
        interests: "Centres d'intérêt",
        languages: 'Langues',
        location: 'Localisation',
        profiles: 'Profils',
        projects: 'Projets',
        publications: 'Publications',
        references: 'Références',
        skills: 'Compétences',
        volunteer: 'Bénévolat',
        work: 'Expérience professionnelle',
      },
      skills: {
        Novice: 'Novice',
        Beginner: 'Débutant',
        Intermediate: 'Intermédiaire',
        Advanced: 'Avancé',
        Expert: 'Expert',
        Master: 'Maître',
      },
    },
    no: {
      countries: NorwegianCountryNames,
      degrees: {
        'Middle School': 'Ungdomsskole',
        'High School': 'Videregående skole',
        Diploma: 'Fagbrev',
        Associate: 'Associate',
        Bachelor: 'Bachelor',
        Master: 'Master',
        Doctor: 'Doktor',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albansk',
        Amharic: 'Amharisk',
        Arabic: 'Arabisk',
        Azerbaijani: 'Aserbajdsjansk',
        Belarusian: 'Belarusisk',
        Bengali: 'Bengali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgarsk',
        Burmese: 'Burmesisk',
        Cantonese: 'Kantonesisk',
        Catalan: 'Katalansk',
        Chinese: 'Kinesisk',
        Croatian: 'Kroatisk',
        Czech: 'Tsjekkisk',
        Danish: 'Dansk',
        Dutch: 'Nederlandsk',
        English: 'Engelsk',
        Estonian: 'Estisk',
        Farsi: 'Farsi',
        Filipino: 'Filippinsk',
        Finnish: 'Finsk',
        French: 'Fransk',
        German: 'Tysk',
        Greek: 'Gresk',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebraisk',
        Hindi: 'Hindi',
        Hungarian: 'Ungarsk',
        Icelandic: 'Islandsk',
        Igbo: 'Igbo',
        Indonesian: 'Indonesisk',
        Irish: 'Irsk',
        Italian: 'Italiensk',
        Japanese: 'Japansk',
        Javanese: 'Javanesisk',
        Kazakh: 'Kasakhisk',
        Khmer: 'Khmer',
        Korean: 'Koreansk',
        Lahnda: 'Lahnda',
        Latvian: 'Latvisk',
        Lithuanian: 'Litauisk',
        Malay: 'Malayisk',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Nepali',
        Norwegian: 'Norsk',
        Oromo: 'Oromo',
        Pashto: 'Pashto',
        Polish: 'Polsk',
        Portuguese: 'Portugisisk',
        Romanian: 'Rumensk',
        Russian: 'Russisk',
        Serbian: 'Serbisk',
        Shona: 'Shona',
        Sinhala: 'Singalesisk',
        Slovak: 'Slovakisk',
        Slovene: 'Slovensk',
        Somali: 'Somali',
        Spanish: 'Spansk',
        Sundanese: 'Sundanesisk',
        Swahili: 'Swahili',
        Swedish: 'Svensk',
        Tagalog: 'Tagalog',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Thai',
        Turkish: 'Tyrkisk',
        Ukrainian: 'Ukrainsk',
        Urdu: 'Urdu',
        Uzbek: 'Usbekisk',
        Vietnamese: 'Vietnamesisk',
        Yoruba: 'Yoruba',
        Zulu: 'Zulu',
      },
      fluency: {
        'Elementary Proficiency': 'Grunnleggende nivå',
        'Limited Working Proficiency': 'Begrenset arbeidskunnskap',
        'Minimum Professional Proficiency': 'Minimum profesjonelt nivå',
        'Full Professional Proficiency': 'Fullt profesjonelt nivå',
        'Native or Bilingual Proficiency': 'Morsmål eller tospråklig nivå',
      },
      sections: {
        awards: 'Utmerkelser',
        basics: 'Grunnleggende informasjon',
        certificates: 'Sertifikater',
        education: 'Utdanning',
        interests: 'Interesser',
        languages: 'Språk',
        location: 'Plassering',
        profiles: 'Profiler',
        projects: 'Prosjekter',
        publications: 'Publikasjoner',
        references: 'Referanser',
        skills: 'Ferdigheter',
        volunteer: 'Frivillig arbeid',
        work: 'Arbeidserfaring',
      },
      skills: {
        Novice: 'Nybegynner',
        Beginner: 'Begynner',
        Intermediate: 'Viderekommen',
        Advanced: 'Avansert',
        Expert: 'Ekspert',
        Master: 'Mester',
      },
    },
    nl: {
      countries: DutchCountryNames,
      degrees: {
        'Middle School': 'Middelbaar',
        'High School': 'Hogeschool',
        Diploma: 'Diploma',
        Associate: 'Graduaat',
        Bachelor: 'Bachelor',
        Master: 'Master',
        Doctor: 'Doctoraat',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albanees',
        Amharic: 'Amhaars',
        Arabic: 'Arabisch',
        Azerbaijani: 'Azerbeidzjaans',
        Belarusian: 'Wit-Russisch',
        Bengali: 'Bengaals',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgaars',
        Burmese: 'Birmees',
        Cantonese: 'Kantonees',
        Catalan: 'Catalaans',
        Chinese: 'Chinees',
        Croatian: 'Kroatisch',
        Czech: 'Tsjechisch',
        Danish: 'Deens',
        Dutch: 'Nederlands',
        English: 'Engels',
        Estonian: 'Ests',
        Farsi: 'Persan',
        Filipino: 'Filipijns',
        Finnish: 'Fins',
        French: 'Frans',
        German: 'Duits',
        Greek: 'Grieks',
        Gujarati: 'Gujarati',
        Hausa: 'Haoussa',
        Hebrew: 'Hebreeuws',
        Hindi: 'Hindi',
        Hungarian: 'Hongaars',
        Icelandic: 'IJslands',
        Igbo: 'Igbo',
        Indonesian: 'Indonesisch',
        Irish: 'Iers',
        Italian: 'Italiaans',
        Japanese: 'Japans',
        Javanese: 'Javaans',
        Kazakh: 'Kazachs',
        Khmer: 'Khmer',
        Korean: 'Koreaans',
        Lahnda: 'Lahnda',
        Latvian: 'Lets',
        Lithuanian: 'Litouws',
        Malay: 'Maleis',
        Mandarin: 'Mandarijn',
        Marathi: 'Marathi',
        Nepali: 'Nepalees',
        Norwegian: 'Noors',
        Oromo: 'Oromo',
        Pashto: 'Pasjto',
        Polish: 'Pools',
        Portuguese: 'Portugees',
        Romanian: 'Roemeense',
        Russian: 'Russisch',
        Serbian: 'Servisch',
        Shona: 'Shona',
        Sinhala: 'Singalees',
        Slovak: 'Slowaaks',
        Slovene: 'Sloveens',
        Somali: 'Somalisch',
        Spanish: 'Spaans',
        Sundanese: 'Soendanees',
        Swahili: 'Swahili',
        Swedish: 'Zweeds',
        Tagalog: 'Tagalog',
        Tamil: 'Tamoul',
        Telugu: 'Telugu',
        Thai: 'Thais',
        Turkish: 'Turks',
        Ukrainian: 'Oekraïens',
        Urdu: 'Urdu',
        Uzbek: 'Oezbeeks',
        Vietnamese: 'Vietnamees',
        Yoruba: 'Joruba',
        Zulu: 'Zoeloe',
      },
      fluency: {
        'Elementary Proficiency': 'Elementaire vaardigheid',
        'Limited Working Proficiency': 'Beperkte werkvaardigheid',
        'Minimum Professional Proficiency':
          'Minimale professionele vaardigheid',
        'Full Professional Proficiency': 'Volledige professionele vaardigheid',
        'Native or Bilingual Proficiency':
          'Moedertaal of tweetalige vaardigheid',
      },
      sections: {
        awards: 'Onderscheidingen',
        basics: 'Basis Informatie',
        certificates: 'Certificaten',
        education: 'Opleidingen',
        interests: 'Interesses',
        languages: 'Talen',
        location: 'Locatie',
        profiles: 'Profielen',
        projects: 'Projecten',
        publications: 'Publicaties',
        references: 'Referenties',
        skills: 'Vaardigheden',
        volunteer: 'Vrijwilligerswerk',
        work: 'Werkervaring',
      },
      skills: {
        Novice: 'Nieuweling',
        Beginner: 'Beginner',
        Intermediate: 'Intermediair',
        Advanced: 'Geavanceerd',
        Expert: 'Expert',
        Master: 'Meester',
      },
    },
    ja: {
      countries: JapaneseCountryNames,
      degrees: {
        'Middle School': '中学校',
        'High School': '高等学校',
        Diploma: '短期大学・高等専門学校',
        Associate: '準学士',
        Bachelor: '学士',
        Master: '修士',
        Doctor: '博士',
      },
      fluency: {
        'Elementary Proficiency': '初級レベル',
        'Limited Working Proficiency': '限定的な実務レベル',
        'Minimum Professional Proficiency': '実務レベル',
        'Full Professional Proficiency': '完全な実務レベル',
        'Native or Bilingual Proficiency': 'ネイティブ・母国語レベル',
      },
      languages: {
        Afrikaans: 'アフリカーンス語',
        Albanian: 'アルバニア語',
        Amharic: 'アムハラ語',
        Arabic: 'アラビア語',
        Azerbaijani: 'アゼルバイジャン語',
        Belarusian: 'ベラルーシ語',
        Bengali: 'ベンガル語',
        Bhojpuri: 'ボージュプリー語',
        Bulgarian: 'ブルガリア語',
        Burmese: 'ビルマ語',
        Cantonese: '広東語',
        Catalan: 'カタロニア語',
        Chinese: '中国語',
        Croatian: 'クロアチア語',
        Czech: 'チェコ語',
        Danish: 'デンマーク語',
        Dutch: 'オランダ語',
        English: '英語',
        Estonian: 'エストニア語',
        Farsi: 'ペルシャ語',
        Filipino: 'フィリピン語',
        Finnish: 'フィンランド語',
        French: 'フランス語',
        German: 'ドイツ語',
        Greek: 'ギリシャ語',
        Gujarati: 'グジャラート語',
        Hausa: 'ハウサ語',
        Hebrew: 'ヘブライ語',
        Hindi: 'ヒンディー語',
        Hungarian: 'ハンガリー語',
        Icelandic: 'アイスランド語',
        Igbo: 'イボ語',
        Indonesian: 'インドネシア語',
        Irish: 'アイルランド語',
        Italian: 'イタリア語',
        Japanese: '日本語',
        Javanese: 'ジャワ語',
        Kazakh: 'カザフ語',
        Khmer: 'クメール語',
        Korean: '韓国語',
        Lahnda: 'ラーンダー語',
        Latvian: 'ラトビア語',
        Lithuanian: 'リトアニア語',
        Malay: 'マレー語',
        Mandarin: '標準中国語',
        Marathi: 'マラーティー語',
        Nepali: 'ネパール語',
        Norwegian: 'ノルウェー語',
        Oromo: 'オロモ語',
        Pashto: 'パシュトー語',
        Polish: 'ポーランド語',
        Portuguese: 'ポルトガル語',
        Romanian: 'ルーマニア語',
        Russian: 'ロシア語',
        Serbian: 'セルビア語',
        Shona: 'ショナ語',
        Sinhala: 'シンハラ語',
        Slovak: 'スロバキア語',
        Slovene: 'スロベニア語',
        Somali: 'ソマリ語',
        Spanish: 'スペイン語',
        Sundanese: 'スンダ語',
        Swahili: 'スワヒリ語',
        Swedish: 'スウェーデン語',
        Tagalog: 'タガログ語',
        Tamil: 'タミル語',
        Telugu: 'テルグ語',
        Thai: 'タイ語',
        Turkish: 'トルコ語',
        Ukrainian: 'ウクライナ語',
        Urdu: 'ウルドゥー語',
        Uzbek: 'ウズベク語',
        Vietnamese: 'ベトナム語',
        Yoruba: 'ヨルバ語',
        Zulu: 'ズールー語',
      },
      sections: {
        awards: '受賞・表彰',
        basics: '基本情報',
        certificates: '資格・免状',
        education: '学歴',
        interests: '興味・関心',
        languages: '言語',
        location: '住所',
        profiles: 'プロフィール',
        projects: 'プロジェクト',
        publications: '出版・著作',
        references: '推薦人',
        skills: 'スキル',
        volunteer: 'ボランティア活動',
        work: '職歴',
      },
      skills: {
        Novice: '入門',
        Beginner: '初級',
        Intermediate: '中級',
        Advanced: '上級',
        Expert: 'エキスパート',
        Master: 'マスター',
      },
    },
    de: {
      countries: GermanCountryNames,
      degrees: {
        'Middle School': 'Mittlere Reife',
        'High School': 'Abitur',
        Diploma: 'Diplom',
        Associate: 'Fachhochschulabschluss',
        Bachelor: 'Bachelor',
        Master: 'Master',
        Doctor: 'Doktor',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albanisch',
        Amharic: 'Amharisch',
        Arabic: 'Arabisch',
        Azerbaijani: 'Aserbaidschanisch',
        Belarusian: 'Belarussisch',
        Bengali: 'Bengalisch',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgarisch',
        Burmese: 'Birmanisch',
        Cantonese: 'Kantonesisch',
        Catalan: 'Katalanisch',
        Chinese: 'Chinesisch',
        Croatian: 'Kroatisch',
        Czech: 'Tschechisch',
        Danish: 'Dänisch',
        Dutch: 'Niederländisch',
        English: 'Englisch',
        Estonian: 'Estnisch',
        Farsi: 'Farsi',
        Filipino: 'Philippinisch',
        Finnish: 'Finnisch',
        French: 'Französisch',
        German: 'Deutsch',
        Greek: 'Griechisch',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebräisch',
        Hindi: 'Hindi',
        Hungarian: 'Ungarisch',
        Icelandic: 'Isländisch',
        Igbo: 'Igbo',
        Indonesian: 'Indonesisch',
        Irish: 'Irisch',
        Italian: 'Italienisch',
        Japanese: 'Japanisch',
        Javanese: 'Javanisch',
        Kazakh: 'Kasachisch',
        Khmer: 'Khmer',
        Korean: 'Koreanisch',
        Lahnda: 'Lahnda',
        Latvian: 'Lettisch',
        Lithuanian: 'Litauisch',
        Malay: 'Malaiisch',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Nepalesisch',
        Norwegian: 'Norwegisch',
        Oromo: 'Oromo',
        Pashto: 'Paschtu',
        Polish: 'Polnisch',
        Portuguese: 'Portugiesisch',
        Romanian: 'Rumänisch',
        Russian: 'Russisch',
        Serbian: 'Serbisch',
        Shona: 'Shona',
        Sinhala: 'Singhalesisch',
        Slovak: 'Slowakisch',
        Slovene: 'Slowenisch',
        Somali: 'Somalisch',
        Spanish: 'Spanisch',
        Sundanese: 'Sundanesisch',
        Swahili: 'Suaheli',
        Swedish: 'Schwedisch',
        Tagalog: 'Tagalog',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Thailändisch',
        Turkish: 'Türkisch',
        Ukrainian: 'Ukrainisch',
        Urdu: 'Urdu',
        Uzbek: 'Usbekisch',
        Vietnamese: 'Vietnamesisch',
        Yoruba: 'Yoruba',
        Zulu: 'Zulu',
      },
      fluency: {
        'Elementary Proficiency': 'Grundkenntnisse',
        'Limited Working Proficiency': 'Gute Kenntnisse',
        'Minimum Professional Proficiency': 'Berufliche Grundkenntnisse',
        'Full Professional Proficiency': 'Verhandlungssichere Kenntnisse',
        'Native or Bilingual Proficiency': 'Muttersprache oder zweisprachig',
      },
      sections: {
        awards: 'Auszeichnungen',
        basics: 'Basisinformationen',
        certificates: 'Zertifikate',
        education: 'Ausbildung',
        interests: 'Interessen',
        languages: 'Sprachen',
        location: 'Ort',
        profiles: 'Profile',
        projects: 'Projekte',
        publications: 'Veröffentlichungen',
        references: 'Referenzen',
        skills: 'Fähigkeiten',
        volunteer: 'Ehrenamtliche Tätigkeiten',
        work: 'Berufserfahrung',
      },
      skills: {
        Novice: 'Einsteiger',
        Beginner: 'Anfänger',
        Intermediate: 'Fortgeschritten',
        Advanced: 'Erfahren',
        Expert: 'Experte',
        Master: 'Spezialist',
      },
    },
    id: {
      countries: IndonesianCountryNames,
      degrees: {
        'Middle School': 'SMP',
        'High School': 'SMA',
        Diploma: 'Diploma',
        Associate: 'Diploma',
        Bachelor: 'Sarjana',
        Master: 'Magister',
        Doctor: 'Doktor',
      },
      fluency: {
        'Elementary Proficiency': 'Tingkat Dasar',
        'Limited Working Proficiency': 'Tingkat Kerja Terbatas',
        'Minimum Professional Proficiency': 'Tingkat Profesional Minimum',
        'Full Professional Proficiency': 'Tingkat Profesional Penuh',
        'Native or Bilingual Proficiency': 'Bahasa Ibu / Bilingual',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albania',
        Amharic: 'Amhara',
        Arabic: 'Arab',
        Azerbaijani: 'Azerbaijan',
        Belarusian: 'Belarus',
        Bengali: 'Benggali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgaria',
        Burmese: 'Burma',
        Cantonese: 'Kanton',
        Catalan: 'Katalan',
        Chinese: 'Tionghoa',
        Croatian: 'Kroasia',
        Czech: 'Ceko',
        Danish: 'Denmark',
        Dutch: 'Belanda',
        English: 'Inggris',
        Estonian: 'Estonia',
        Farsi: 'Persia',
        Filipino: 'Filipina',
        Finnish: 'Finlandia',
        French: 'Prancis',
        German: 'Jerman',
        Greek: 'Yunani',
        Gujarati: 'Gujarat',
        Hausa: 'Hausa',
        Hebrew: 'Ibrani',
        Hindi: 'Hindi',
        Hungarian: 'Hungaria',
        Icelandic: 'Islandia',
        Igbo: 'Igbo',
        Indonesian: 'Indonesia',
        Irish: 'Irlandia',
        Italian: 'Italia',
        Japanese: 'Jepang',
        Javanese: 'Jawa',
        Kazakh: 'Kazakh',
        Khmer: 'Khmer',
        Korean: 'Korea',
        Lahnda: 'Lahnda',
        Latvian: 'Latvia',
        Lithuanian: 'Lituania',
        Malay: 'Melayu',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Nepal',
        Norwegian: 'Norwegia',
        Oromo: 'Oromo',
        Pashto: 'Pashto',
        Polish: 'Polandia',
        Portuguese: 'Portugis',
        Romanian: 'Rumania',
        Russian: 'Rusia',
        Serbian: 'Serbia',
        Shona: 'Shona',
        Sinhala: 'Sinhala',
        Slovak: 'Slovakia',
        Slovene: 'Slovenia',
        Somali: 'Somali',
        Spanish: 'Spanyol',
        Sundanese: 'Sunda',
        Swahili: 'Swahili',
        Swedish: 'Swedia',
        Tagalog: 'Tagalog',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Thailand',
        Turkish: 'Turki',
        Ukrainian: 'Ukraina',
        Urdu: 'Urdu',
        Uzbek: 'Uzbek',
        Vietnamese: 'Vietnam',
        Yoruba: 'Yoruba',
        Zulu: 'Zulu',
      },
      sections: {
        awards: 'Penghargaan',
        basics: 'Informasi Dasar',
        certificates: 'Sertifikat',
        education: 'Pendidikan',
        interests: 'Minat',
        languages: 'Bahasa',
        location: 'Lokasi',
        profiles: 'Profil',
        projects: 'Proyek',
        publications: 'Publikasi',
        references: 'Referensi',
        skills: 'Keahlian',
        volunteer: 'Kegiatan Sukarela',
        work: 'Pengalaman Kerja',
      },
      skills: {
        Novice: 'Pemula',
        Beginner: 'Dasar',
        Intermediate: 'Menengah',
        Advanced: 'Mahir',
        Expert: 'Ahli',
        Master: 'Master',
      },
    },
    'pt-br': {
      countries: BrazilianPortugueseCountryNames,
      degrees: {
        'Middle School': 'Ensino Fundamental',
        'High School': 'Ensino Médio',
        Diploma: 'Diploma',
        Associate: 'Tecnólogo',
        Bachelor: 'Bacharelado',
        Master: 'Mestrado',
        Doctor: 'Doutorado',
      },
      fluency: {
        'Elementary Proficiency': 'Proficiência Básica',
        'Limited Working Proficiency': 'Proficiência Profissional Limitada',
        'Minimum Professional Proficiency': 'Proficiência Profissional Mínima',
        'Full Professional Proficiency': 'Proficiência Profissional Fluente',
        'Native or Bilingual Proficiency': 'Nativo ou Bilíngue',
      },
      languages: {
        Afrikaans: 'Africander',
        Albanian: 'Albanês',
        Amharic: 'Amárico',
        Arabic: 'Árabe',
        Azerbaijani: 'Azerbaijano',
        Belarusian: 'Bielorrusso',
        Bengali: 'Bengali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Búlgaro',
        Burmese: 'Birmanês',
        Cantonese: 'Cantonês',
        Catalan: 'Catalão',
        Chinese: 'Chinês',
        Croatian: 'Croata',
        Czech: 'Tcheco',
        Danish: 'Dinamarquês',
        Dutch: 'Holandês',
        English: 'Inglês',
        Estonian: 'Estoniano',
        Farsi: 'Persa',
        Filipino: 'Filipino',
        Finnish: 'Finlandês',
        French: 'Francês',
        German: 'Alemão',
        Greek: 'Grego',
        Gujarati: 'Guzerate',
        Hausa: 'Hausa',
        Hebrew: 'Hebraico',
        Hindi: 'Hindi',
        Hungarian: 'Húngaro',
        Icelandic: 'Islandês',
        Igbo: 'Igbo',
        Indonesian: 'Indonésio',
        Irish: 'Irlandês',
        Italian: 'Italiano',
        Japanese: 'Japonês',
        Javanese: 'Javanês',
        Kazakh: 'Cazaque',
        Khmer: 'Khmer',
        Korean: 'Coreano',
        Lahnda: 'Lahnda',
        Latvian: 'Letão',
        Lithuanian: 'Lituano',
        Malay: 'Malaio',
        Mandarin: 'Mandarim',
        Marathi: 'Marathi',
        Nepali: 'Nepalês',
        Norwegian: 'Norueguês',
        Oromo: 'Oromo',
        Pashto: 'Pashto',
        Polish: 'Polonês',
        Portuguese: 'Português',
        Romanian: 'Romeno',
        Russian: 'Russo',
        Serbian: 'Sérvio',
        Shona: 'Shona',
        Sinhala: 'Cingalês',
        Slovak: 'Eslovaco',
        Slovene: 'Esloveno',
        Somali: 'Somali',
        Spanish: 'Espanhol',
        Sundanese: 'Sundanês',
        Swahili: 'Suaíli',
        Swedish: 'Sueco',
        Tagalog: 'Tagalo',
        Tamil: 'Tamil',
        Telugu: 'Telugo',
        Thai: 'Tailandês',
        Turkish: 'Turco',
        Ukrainian: 'Ucraniano',
        Urdu: 'Urdu',
        Uzbek: 'Uzbeque',
        Vietnamese: 'Vietnamita',
        Yoruba: 'Ioruba',
        Zulu: 'Zulu',
      },
      sections: {
        awards: 'Prêmios',
        basics: 'Informações Básicas',
        certificates: 'Certificados',
        education: 'Formação',
        interests: 'Interesses',
        languages: 'Idiomas',
        location: 'Localização',
        profiles: 'Perfis',
        projects: 'Projetos',
        publications: 'Publicações',
        references: 'Referências',
        skills: 'Competências',
        volunteer: 'Voluntariado',
        work: 'Experiência Profissional',
      },
      skills: {
        Novice: 'Novato',
        Beginner: 'Iniciante',
        Intermediate: 'Intermediário',
        Advanced: 'Avançado',
        Expert: 'Especialista',
        Master: 'Mestre',
      },
    },
  }

  return get(
    optionsTranslations,
    [language, category, option],
    option
  ) as string
}
